import { AppDispatch, AppThunk, GetState } from '@om-fe/core/core/types';
import {
    apiSubscribeAction,
    apiUnsubscribeAction,
    attachReducerAction,
    detachReducerAction,
} from '@om-fe/core/utils/action';
import { filterNullParams } from '@om-fe/core/utils/router';
import * as shallowEqual from 'shallowequal';
import getSlice from '@om-fe/core/utils/get-slice';
import {
    FavoriteMatchListActionsTypes,
    FavoriteMatchListSetFilterDataAction,
    FavoriteMatchListSetLoadingData,
    FavoriteMatchListStoreModel,
    FavoriteMatchListTopic,
} from './favorite-match-list.constants';
import { getFavoriteMatchListSliceName, getFavoriteMatchListTopic } from './favorite-match-list.utils';
import { favoriteMatchListReducerCreator } from './favorite-match-list.reducer';
import { DisciplineLiveStatus } from '@om-fe/models';

export const favoriteMatchListSetLoadingData = (slice: string, isLoadingMoreData: boolean = false): FavoriteMatchListSetLoadingData => ({
    type: FavoriteMatchListActionsTypes.SetLoadingData,
    meta: {
        slice,
    },
    payload: {
        isLoadingMoreData,
    },
});

export const favoriteMatchListSetFilterData = (
    slice: string,
    userID: number,
    liveStatus: DisciplineLiveStatus,
    isDefault?: boolean,
    mainMarketsLimit?: number,
): FavoriteMatchListSetFilterDataAction => ({
    type: FavoriteMatchListActionsTypes.SetFilterData,
    meta: {
        slice,
    },
    payload: {
        userID,
        liveStatus,
        isDefault,
        mainMarketsLimit,
    },
});

export const getFavoriteMatchList = (
    instanceId: string,
    userID: number,
    liveStatus: DisciplineLiveStatus,
    isDefault: boolean | undefined,
    mainMarketsLimit: number | undefined,
): AppThunk => (dispatch: AppDispatch) => {
    const slice: string = getFavoriteMatchListSliceName(instanceId);
    const topic: FavoriteMatchListTopic = getFavoriteMatchListTopic(
        userID,
        liveStatus,
        isDefault,
        mainMarketsLimit,
    );

    dispatch(attachReducerAction(slice, favoriteMatchListReducerCreator(instanceId)));

    dispatch(favoriteMatchListSetFilterData(
        slice,
        userID,
        liveStatus,
        isDefault,
        mainMarketsLimit,
    ));

    dispatch(apiSubscribeAction(topic.path, filterNullParams(topic.params), slice));
};

export const removeFavoriteMatchList = (
    instanceId: string,
): AppThunk => (dispatch: AppDispatch, getState: GetState) => {
    const slice: string = getFavoriteMatchListSliceName(instanceId);
    const favoriteMatchListSlice: FavoriteMatchListStoreModel | undefined = getSlice<FavoriteMatchListStoreModel>(getState(), slice);

    if (!favoriteMatchListSlice) {
        return;
    }

    const topic: FavoriteMatchListTopic = getFavoriteMatchListTopic(
        favoriteMatchListSlice.userID!,
        favoriteMatchListSlice.liveStatus,
        favoriteMatchListSlice.isDefault,
        favoriteMatchListSlice.mainMarketsLimit,
    );

    dispatch(detachReducerAction(slice));
    dispatch(apiUnsubscribeAction(topic.path, filterNullParams(topic.params), slice));
};

export const updateFavoriteMatchList = (
    instanceId: string,
    userID: number,
    liveStatus: DisciplineLiveStatus,
    isDefault: boolean | undefined,
    mainMarketsLimit: number | undefined,
): AppThunk => (dispatch: AppDispatch, getState: GetState): void => {
    const slice: string = getFavoriteMatchListSliceName(instanceId);
    const favoriteMatchListSlice: FavoriteMatchListStoreModel | undefined = getSlice<FavoriteMatchListStoreModel>(getState(), slice);

    if (!favoriteMatchListSlice) {
        return;
    }

    const oldTopic: FavoriteMatchListTopic = getFavoriteMatchListTopic(
        favoriteMatchListSlice.userID!,
        favoriteMatchListSlice.liveStatus,
        favoriteMatchListSlice.isDefault,
        favoriteMatchListSlice.mainMarketsLimit,
    );

    const newTopic: FavoriteMatchListTopic = getFavoriteMatchListTopic(
        userID,
        liveStatus,
        isDefault,
        mainMarketsLimit,
    );

    const isLoadingMoreData: boolean = shallowEqual(oldTopic, newTopic);
    dispatch(favoriteMatchListSetLoadingData(slice, isLoadingMoreData));

    dispatch(favoriteMatchListSetFilterData(
        slice,
        userID,
        liveStatus,
        isDefault,
        mainMarketsLimit,
    ));

    dispatch(apiUnsubscribeAction(oldTopic.path, filterNullParams(oldTopic.params), slice));
    dispatch(apiSubscribeAction(newTopic.path, filterNullParams(newTopic.params), slice));
};
